import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { Form } from 'react-bootstrap';
import { IFormField } from 'modules/core/types';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import { useUncontrolledInput } from 'modules/core/hooks';
import { ASTERISK, EMPTY_STRING } from 'modules/core/constants';

import { Input as InputUI, InputFontWeight, Adornment } from '../Input/styles';
import { CalendarUI } from './styles';
import { CALENDAR_INPUT_CLASS_NAME, DATE_FORMAT } from './calendarInput.const';
import { CalendarAdornment } from '../Adornments/CalendarAdornment/CalendarAdornment';

// We need to declare alias in order
// to avoid cycled dependency in core types.s
export interface ICalendarInput extends IFormField {
  textPosition?: 'end' | 'start';
  textStyle?: InputFontWeight;
  testId?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  maxDate?: Date;
  autoComplete?: boolean;
  changeCallback?: (event?: any) => void;
  isInvalid?: boolean;
  minDate?: Date;
}

export const CalendarInput: React.FC<ICalendarInput> = ({
  name,
  defaultValue,
  validate,
  rules = {},
  textPosition = 'start',
  textStyle = 'normal',
  testId,
  disabled,
  required,
  placeholder,
  maxDate,
  autoComplete = false,
  changeCallback,
  isInvalid = false,
  minDate,
}) => {
  const { hasError, errorMessage, controlField, setValue, fieldValue } = useUncontrolledInput(name);
  const calendarRef = useRef<DatePicker>(null);

  const handleCalendarInputChange = useCallback(
    (onChange) => (value: Date) => {
      setValue(name, value);

      changeCallback && changeCallback(value);

      return onChange(value);
    },
    [setValue, name, changeCallback],
  );

  useEffect(() => {
    if ((minDate && fieldValue < minDate) || (maxDate && fieldValue > maxDate)) {
      setValue(name, NaN);
    }
  }, [minDate, maxDate, fieldValue, setValue, name]);

  const processedPlaceholder = required ? `${placeholder}${ASTERISK}` : placeholder;

  const commonInputProps = useMemo(
    () => ({
      placeholder: processedPlaceholder,
      name,
      isInvalid: Boolean(errorMessage) || hasError || isInvalid,
      disabled,
    }),
    [disabled, errorMessage, hasError, name, processedPlaceholder, isInvalid],
  );

  const renderCalendar = useCallback(
    ({ onChange }: ControllerRenderProps) => (
      <CalendarUI
        {...commonInputProps}
        ref={calendarRef}
        className={CALENDAR_INPUT_CLASS_NAME}
        placeholderText={DATE_FORMAT.toUpperCase()}
        dateFormat={DATE_FORMAT}
        selected={fieldValue || (defaultValue as Date)}
        onChange={handleCalendarInputChange(onChange)}
        showMonthDropdown
        showYearDropdown
        maxDate={maxDate}
        minDate={minDate}
        autoComplete={autoComplete ? 'on' : 'off'}
      />
    ),
    [commonInputProps, defaultValue, handleCalendarInputChange, maxDate, autoComplete, fieldValue, minDate],
  );

  return (
    <>
      <InputUI
        textposition={textPosition}
        textstyle={textStyle}
        data-testid={testId}
        $isInvalid={commonInputProps.isInvalid}
        $endAdornment={true}
        $isDisabled={disabled}
      >
        <Controller
          render={renderCalendar}
          defaultValue={defaultValue || EMPTY_STRING}
          control={controlField}
          name={name}
          rules={{
            ...rules,
            validate,
          }}
        />
        <Adornment $isCursorPointer={true} onClick={() => (!disabled ? calendarRef.current?.setOpen(true) : undefined)}>
          <CalendarAdornment />
        </Adornment>
      </InputUI>
      <Form.Control.Feedback
        type="invalid"
        className={`${commonInputProps.isInvalid && errorMessage ? 'd-block' : 'd-none'}`}
      >
        {errorMessage}
      </Form.Control.Feedback>
    </>
  );
};
